<template>
  <div class="max-w-[900px] mx-auto py-5 animate-fade-in">
    <PageHeader title="站点设置" subtitle="管理站点基础配置、安全策略及第三方集成">
      <template #icon><Setting /></template>
      <template #actions>
        <el-button :icon="Refresh" plain @click="loadAllSettings" class="hover-lift">
          重新加载
        </el-button>
      </template>
    </PageHeader>

    <!-- Site Config -->
    <UiCard class="mb-6" shadow="never">
      <template #header>
        <div class="flex justify-between items-center">
          <div class="flex items-center gap-2 font-semibold text-[var(--color-heading)]">
            <el-icon><Monitor /></el-icon>
            <span>基础设置</span>
            <DirtyTag :visible="hasGroupChanges('site')" />
          </div>
          <el-button
            type="primary"
            size="small"
            @click="saveGroup('site')"
            :loading="saving.site"
            class="hover-lift"
            >保存</el-button
          >
        </div>
      </template>
      <el-form label-position="top" :model="settings.site">
        <el-row :gutter="20">
          <el-col :xs="24" :sm="8">
            <el-form-item label="允许新用户注册">
              <el-switch v-model="settings.site.allow_register" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="8">
            <el-form-item label="强制邀请码">
              <el-switch v-model="settings.site.require_invite" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="8">
            <el-form-item label="启用公共皮肤库">
              <el-switch v-model="settings.site.enable_skin_library" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="新建角色 UUID 生成方式">
          <el-radio-group
            v-model="settings.site.profile_uuid_mode"
            class="inline-flex flex-wrap gap-2"
          >
            <el-radio-button value="random">随机 UUID</el-radio-button>
            <el-radio-button value="offline">离线 UUID</el-radio-button>
          </el-radio-group>
          <span class="text-xs text-[var(--color-text-light)] leading-normal mt-1 block ml-4"
            >默认使用随机 UUID（uuid4）；启用后按角色名生成离线 UUID。</span
          >
        </el-form-item>
        <el-form-item label="最大纹理大小 (KB)">
          <el-input-number v-model="settings.site.max_texture_size" :min="64" :step="128" />
        </el-form-item>
        <el-divider />
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="站点名称">
              <el-input v-model="settings.site.site_name" placeholder="皮肤站" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="站点副标题">
              <el-input
                v-model="settings.site.site_subtitle"
                placeholder="简洁、高效、现代的 Minecraft 皮肤管理站"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="页脚附加信息">
              <el-input
                v-model="settings.site.footer_text"
                placeholder="Copyright © 2026 Element Skin"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="24">
            <el-collapse class="regulatory-section" v-model="regulatoryCollapse">
              <el-collapse-item title="监管信息" name="regulatory-info">
                <el-row :gutter="20">
                  <el-col :xs="24" :sm="12">
                    <el-form-item label="ICP 备案信息">
                      <el-input v-model="settings.site.filing_icp" placeholder="留空则不展示" />
                    </el-form-item>
                  </el-col>
                  <el-col :xs="24" :sm="12">
                    <el-form-item label="ICP 备案链接">
                      <el-input
                        v-model="settings.site.filing_icp_link"
                        placeholder="留空则不展示"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :xs="24" :sm="12">
                    <el-form-item label="公安备案信息">
                      <el-input v-model="settings.site.filing_mps" placeholder="留空则不展示" />
                    </el-form-item>
                  </el-col>
                  <el-col :xs="24" :sm="12">
                    <el-form-item label="公安备案链接">
                      <el-input
                        v-model="settings.site.filing_mps_link"
                        placeholder="留空则不展示"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-collapse-item>
            </el-collapse>
          </el-col>
        </el-row>
      </el-form>
    </UiCard>

    <!-- Security Config -->
    <UiCard class="mb-6" shadow="never">
      <template #header>
        <div class="flex justify-between items-center">
          <div class="flex items-center gap-2 font-semibold text-[var(--color-heading)]">
            <el-icon><Lock /></el-icon>
            <span>安全与速率限制</span>
            <DirtyTag :visible="hasGroupChanges('security')" />
          </div>
          <el-button
            type="primary"
            size="small"
            @click="saveGroup('security')"
            :loading="saving.security"
            class="hover-lift"
            >保存</el-button
          >
        </div>
      </template>
      <el-form label-position="top" :model="settings.security">
        <el-form-item label="强密码检查">
          <el-switch v-model="settings.security.enable_strong_password_check" />
          <span class="text-xs text-[var(--color-text-light)] leading-normal mt-1 block ml-4"
            >启用后，用户注册或修改密码将执行严格的复杂度检查。</span
          >
        </el-form-item>
        <el-divider />
        <el-form-item label="身份验证速率限制">
          <el-switch v-model="settings.security.rate_limit_enabled" />
          <span class="text-xs text-[var(--color-text-light)] leading-normal mt-1 block ml-4"
            >启用后将限制登录、重置密码等接口的尝试频率。</span
          >
        </el-form-item>
        <el-row :gutter="20" v-if="settings.security.rate_limit_enabled">
          <el-col :xs="24" :sm="12">
            <el-form-item label="尝试次数上限">
              <el-input-number v-model="settings.security.rate_limit_auth_attempts" :min="1" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="时间窗口 (分钟)">
              <el-input-number v-model="settings.security.rate_limit_auth_window" :min="1" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </UiCard>

    <!-- Auth / JWT Config -->
    <UiCard class="mb-6" shadow="never">
      <template #header>
        <div class="flex justify-between items-center">
          <div class="flex items-center gap-2 font-semibold text-[var(--color-heading)]">
            <el-icon><Key /></el-icon>
            <span>令牌与认证 (JWT)</span>
            <DirtyTag :visible="hasGroupChanges('auth')" />
          </div>
          <el-button
            type="primary"
            size="small"
            @click="saveGroup('auth')"
            :loading="saving.auth"
            class="hover-lift"
            >保存</el-button
          >
        </div>
      </template>
      <el-form label-position="top" :model="settings.auth">
        <el-form-item label="令牌有效期 (天)">
          <el-input-number v-model="settings.auth.jwt_expire_days" :min="1" :max="365" />
          <p class="text-xs text-[var(--color-text-light)] leading-normal mt-1 block">
            用户登录后，其身份令牌将在该天数后失效。
          </p>
        </el-form-item>
      </el-form>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, reactive, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh, Setting, Monitor, Lock, Key } from '@element-plus/icons-vue'
import { getAdminSettingsGroup, saveAdminSettingsGroup } from '@/api/admin/settings'
import PageHeader from '@/components/common/PageHeader.vue'
import UiCard from '@/components/ui/UiCard.vue'
import DirtyTag from '@/components/common/DirtyTag.vue'
import { useDirtySnapshot } from '@/composables/useDirtySnapshot'

const settings = reactive({
  site: {
    site_name: '',
    site_subtitle: '',
    require_invite: false,
    allow_register: true,
    enable_skin_library: true,
    profile_uuid_mode: 'random',
    max_texture_size: 1024,
    footer_text: '',
    filing_icp: '',
    filing_icp_link: '',
    filing_mps: '',
    filing_mps_link: '',
  },
  security: {
    rate_limit_enabled: true,
    rate_limit_auth_attempts: 5,
    rate_limit_auth_window: 15,
    enable_strong_password_check: false,
  },
  auth: {
    jwt_expire_days: 7,
  },
})

const saving = reactive({
  site: false,
  security: false,
  auth: false,
})

const regulatoryCollapse = ref<string[]>([])

type SettingsGroup = 'site' | 'security' | 'auth'

const siteDirty = useDirtySnapshot(computed(() => settings.site))
const securityDirty = useDirtySnapshot(computed(() => settings.security))
const authDirty = useDirtySnapshot(computed(() => settings.auth))

function hasGroupChanges(group: SettingsGroup) {
  if (group === 'site') return siteDirty.hasChanges.value
  if (group === 'security') return securityDirty.hasChanges.value
  return authDirty.hasChanges.value
}

function captureGroup(group: SettingsGroup) {
  if (group === 'site') siteDirty.capture()
  else if (group === 'security') securityDirty.capture()
  else authDirty.capture()
}

async function loadGroup(group: SettingsGroup) {
  try {
    const res = await getAdminSettingsGroup(group)
    Object.assign(settings[group], res.data)
    captureGroup(group)
  } catch {
    ElMessage.error(`加载 ${group} 设置失败`)
  }
}

async function loadAllSettings() {
  await Promise.all([loadGroup('site'), loadGroup('security'), loadGroup('auth')])
}

async function saveGroup(group: SettingsGroup) {
  saving[group] = true
  try {
    await saveAdminSettingsGroup(group, settings[group])
    captureGroup(group)
    ElMessage.success('设置已更新')
  } catch {
    ElMessage.error('保存失败')
  } finally {
    saving[group] = false
  }
}

onMounted(loadAllSettings)
</script>
